import { GlobalNavigation } from '@snsw/react-component-library';
import { HeaderPlaceholder } from 'components/Header/HeaderPlaceholder';
import { useLatest } from 'react-use';
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './header.css';

type MaybePromise<T> = T | Promise<T>;
type NavLinkCallback = () => MaybePromise<boolean | null | undefined>;

export interface HeaderOptions {
  logoUrl?: string;
  homeLink?: string;
  homeLinkTarget?: string;
  myServiceAccount?: boolean;
  hideWaratah?: boolean;

  forceShowLogout?: boolean;

  /**
   * Custom URL to handle logout behaviour. Since the underlying header component only supports logging out of a
   * production domain out of the box, this URL can be used to logout of a different domain instead.
   */
  logoutUrlOverride?: string;

  /**
   * Optional handler to invoke before navigating to the logout URL.
   *
   * @returns `false` to prevent logout
   */
  onLoggingOut?: NavLinkCallback;
}

/**
 * The element type exposed by a `ref` passed to the header.
 */
export interface ServiceNswHeader {
  logout: () => void;
}

export interface HeaderProps {
  options?: HeaderOptions;
}

const showHideMobileMenuItems = (header: Element, options: HeaderOptions) => {
  const menuListMobile = header.querySelectorAll('#block-global-header-mobile-menu ul li');

  menuListMobile.forEach((item) => {
    if (options?.myServiceAccount === false) {
      item.classList.add('hideElement');
    } else {
      if (item.classList.value.indexOf('is-myaccount') < 0 && item.classList.value.indexOf('is-logout') < 0) {
        item.classList.add('hideElement');
      } else {
        item.classList.remove('hideElement');
      }
    }
  });
};

const getLogoutLinkContainer = (header: Element) => header.querySelector('.is-logout') as HTMLElement;

const updateHeaderElements = (header: Element, options: HeaderOptions) => {
  //Delete Main Navigation Element
  const mainNavigation = header.querySelector('.global-header__main #block-global-header-menu');
  if (mainNavigation) {
    mainNavigation.parentNode.removeChild(mainNavigation);
  }

  //Delete Header Search Element
  const headerSearch = header.querySelector('.global-header__main .global-header__search');
  if (headerSearch) {
    headerSearch.parentNode.removeChild(headerSearch);
  }

  //Delete Header Search Button for Mobile
  const headerMobileSearch = header.querySelector('.global-header__main .global-header__search-toggle');
  if (headerMobileSearch) {
    headerMobileSearch.parentNode.removeChild(headerMobileSearch);
  }

  //Hide/Show 'few options' Menu Nav from Mobile List
  showHideMobileMenuItems(header, options);

  //Account Menu
  const accountMenu = header.querySelector('#block-global-header-account-menu');
  if (options && options.myServiceAccount == false) {
    accountMenu.classList.add('hideElement');
  } else {
    if (accountMenu && accountMenu.classList.contains('hideElement')) {
      accountMenu.classList.remove('hideElement');
    }
  }

  //setting LogoUrl to Default
  const svgWaratah = header.getElementsByTagName('svg')[0];
  const svgServiceNSW = header.querySelector('.global-header__main svg + span + svg') as SVGElement;
  const divider = header.querySelector('.global-header__main svg + span') as HTMLSpanElement;
  const logoLink = header.querySelector('.global-header__main a');

  let imgLogo = header.querySelector('.global-header__main svg + span + svg + img') as HTMLImageElement;

  if (!options || !options.logoUrl) {
    if (svgServiceNSW && svgServiceNSW.classList.contains('hideElement')) {
      svgServiceNSW.classList.remove('hideElement');
      imgLogo.classList.add('hideElement');
    }
  }

  // Hide Service NSW Logo
  if (options && options.logoUrl) {
    if (svgServiceNSW && svgServiceNSW.classList.contains('hideElement') == false) {
      svgServiceNSW.classList.add('hideElement');

      if (imgLogo == null) {
        // Insert New Logo to replace Service NSW Logo
        imgLogo = document.createElement('img');
        svgServiceNSW.parentNode.insertBefore(imgLogo, svgServiceNSW.nextSibling);
      } else {
        imgLogo.classList.remove('hideElement');
      }
    }

    if (imgLogo) {
      imgLogo.src = options.logoUrl;
    }
  }

  if (options && options.hideWaratah) {
    svgWaratah.classList.add('hideElement');
    divider.classList.add('hideElement');
  } else {
    svgWaratah.classList.remove('hideElement');
    divider.classList.remove('hideElement');
  }

  let nswHomeLink = logoLink?.getAttribute('nswHomeLink');
  if (options && options.homeLink) {
    if (nswHomeLink === undefined) {
      logoLink.setAttribute('nswHomeLink', logoLink.getAttribute('href'));
    }
    logoLink.setAttribute('href', options.homeLink);
  } else {
    if (nswHomeLink) {
      logoLink.setAttribute('href', nswHomeLink);
    }
  }
  if (logoLink && options && options.homeLinkTarget) {
    logoLink.setAttribute('target', options.homeLinkTarget);
  }
};

const overrideHeader = (headerElements: HTMLCollectionOf<Element>, options: HeaderOptions): Element => {
  //delete Footer
  if (headerElements.length > 1) {
    headerElements[1].remove();
  }

  const header = headerElements[0];
  if (header != null) {
    updateHeaderElements(header, options);
    header.classList.add('loaded-header');
  }

  return header;
};

const useHeader = (options?: HeaderOptions) => {
  const [header, setHeader] = useState<Element | undefined>();

  useEffect(() => {
    if (header) return;

    const targetNode = document.getElementsByTagName('body')[0];

    const nswEmbeddedElements = targetNode.getElementsByClassName('servicensw-embed');
    if (nswEmbeddedElements.length > 0) {
      setHeader(nswEmbeddedElements[0]);
      return;
    }

    const config = { childList: true };
    let observer: MutationObserver;

    const handleChildListMutation: MutationCallback = (mutations) => {
      for (const m of mutations) {
        if (m.type !== 'childList') continue;

        const nswEmbeddedElements = targetNode.getElementsByClassName('servicensw-embed');
        if (nswEmbeddedElements.length === 0) continue;

        const headerElement = overrideHeader(nswEmbeddedElements, options);
        setHeader(headerElement);

        observer.disconnect();
        observer = null;
        break;
      }
    };

    observer = new MutationObserver(handleChildListMutation);
    observer.observe(targetNode, config);

    return () => observer?.disconnect();
  }, [header, options]);

  return header;
};

// Overrides the default `display` style for the logout link. The header's default functionality is to only show
// the "Log out" link iff the user is logged in to service.nsw.gov.au domain, which won't work for
// licence.nsw.gov.au (for example).
const useLogoutLinkDisplayToggle = (header: Element, forceShowLogout: HeaderOptions['forceShowLogout']) => {
  const logoutLinkDisplayRef = useRef<string | null>(null);

  useEffect(() => {
    if (!header) return;
    if (forceShowLogout == null) return;

    const logoutMenuItem = getLogoutLinkContainer(header);
    if (!logoutMenuItem) return;

    if (forceShowLogout) {
      // Keep the original display style of the link so that we can restore it if `forceShowLogout` changes
      if (!logoutLinkDisplayRef.current) {
        logoutLinkDisplayRef.current = logoutMenuItem.style.display;
      }

      logoutMenuItem.style.display = 'inherit';
    } else if (logoutLinkDisplayRef.current != null) {
      // Restore back to the original setting - effectively this lets the header manage the display property
      logoutMenuItem.style.display = logoutLinkDisplayRef.current;
      logoutLinkDisplayRef.current = null;
    }
  }, [header, forceShowLogout]);
};

// Overrides the logout link to allow a callback to execute before proceeding to a customisable URL.
const useLogoutLink = (
  header: Element,
  overrideUrl: HeaderOptions['logoutUrlOverride'],
  callback: HeaderOptions['onLoggingOut']
): HTMLAnchorElement => {
  const onLoggingOutRef = useLatest(callback);
  const shouldOverrideLogout = !!(overrideUrl || onLoggingOutRef.current);

  const handleLoggingOut = useCallback(
    async (event: MouseEvent) => {
      let shouldLogout: boolean | undefined;

      event.preventDefault();

      try {
        // await doesn't care if the method returns a promise or not; if it doesn't, it will just convert the
        // returned value to a promise and await it.
        shouldLogout = !onLoggingOutRef.current || (await onLoggingOutRef.current());
      } catch {
        shouldLogout = false;
      }

      // Only prevent logout if the callback threw an error or explicitly returned 'false'.
      if (shouldLogout === false) {
        console.debug('Cancelling logout at the request of the host');
      } else {
        const logoutUrl = overrideUrl || (event.target as HTMLAnchorElement).href;

        // Push to the bottom of the execution stack to allow any other handlers to finish processing.
        setTimeout(() => {
          window.location.href = logoutUrl;
        });
      }
    },
    [overrideUrl]
  );

  const logoutLink = header && getLogoutLinkContainer(header).querySelector('a');

  useEffect(() => {
    if (!(shouldOverrideLogout && logoutLink)) return;

    logoutLink.addEventListener('click', handleLoggingOut);
    logoutLink.addEventListener('keydown', handleLoggingOut);

    return () => {
      logoutLink.removeEventListener('click', handleLoggingOut);
      logoutLink.removeEventListener('keydown', handleLoggingOut);
    };
  }, [logoutLink, shouldOverrideLogout]);

  return logoutLink;
};

const useImperativeHeaderRef = (logoutLink: HTMLAnchorElement | undefined, ref: ForwardedRef<ServiceNswHeader>) => {
  const logoutLinkRef = useLatest(logoutLink);

  return useImperativeHandle(
    ref,
    (): ServiceNswHeader => ({
      logout: () => logoutLinkRef.current?.click(),
    }),
    []
  );
};

export const Header = forwardRef<ServiceNswHeader, HeaderProps>(({ options }, ref) => {
  const header = useHeader(options);
  useLogoutLinkDisplayToggle(header, options?.forceShowLogout);

  const logoutLink = useLogoutLink(header, options?.logoutUrlOverride, options?.onLoggingOut);
  useImperativeHeaderRef(logoutLink, ref);

  return (
    <>
      <GlobalNavigation apiKey="3IGjb84ryJ8MdVrQt0urKLcmEyUjKQzH" />
      {!header && <HeaderPlaceholder />}
    </>
  );
});
